import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  LinearProgress,
  Button,
  Divider,
  CircularProgress
} from '@mui/material';
import { AccountBalance, SwapHoriz, MoneyOff, AttachMoney } from '@mui/icons-material';
import { fetchMemberFinancialDetails, allocateFunds, transferFunds } from './api';
import AllocateFundsDialog from './AllocateFunds';
import CashCollection from './CashCollection';
import TransferFundsDialog from './TransferFundsDialog';
import AccountOffsetDialog from './AccountOffsetDialog';

const FinanceDetails = ({ user }) => {
  const [financialData, setFinancialData] = useState({
    unallocated_funds: 0,
    total_savings: 0,
    pending_loans: 0,
    advance_loans: 0,
    loan_eligibility: 0,
    credit_score: 0
  });
  const [loading, setLoading] = useState(true);
  const [allocateDialogOpen, setAllocateDialogOpen] = useState(false);
  const [cashModalOpen, setCashModalOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [offsetDialogOpen, setOffsetDialogOpen] = useState(false);

  const handleCashModalOpen = () => {
    setCashModalOpen(true);
  };

  const handleCashModalClose = () => {
    setCashModalOpen(false);
  };

  const handleTransferDialogOpen = () => {
    setTransferDialogOpen(true);
  };

  const handleOffsetDialogOpen = () => {
    setOffsetDialogOpen(true);
  };
  
  const handleOffsetDialogClose = () => {
    setOffsetDialogOpen(false);
  };
  
  const handleOffsetComplete = async (result) => {
    try {
      // Refresh financial data after successful offset
      const data = await fetchMemberFinancialDetails(user.id);
      setFinancialData(data);
    } catch (error) {
      console.error('Error refreshing financial data after offset:', error);
    }
  };

  const handleTransferDialogClose = () => {
    setTransferDialogOpen(false);
  };

  const handleCashSubmit = async (cashData, code) => {
    try {
      setLoading(true);
      // Here you would call your API to process the cash collection
      // Example: await processCashCollection(user.id, cashData, code);

      // Refresh financial data after collection
      const data = await fetchMemberFinancialDetails(user.id);
      setFinancialData(data);

      // Optionally show success feedback
      alert(`Cash collection of KES ${cashData.amount} processed successfully!`);
    } catch (error) {
      console.error('Error processing cash collection:', error);
      alert(`Error: ${error.response?.data?.error || "Failed to process cash collection"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTransferFunds = async (transferData) => {
    try {
      setLoading(true);
      
      // Call the transfer funds API
      const response = await transferFunds(user.id, transferData);
      
      if (response.success) {
        // Refresh financial data after successful transfer
        const data = await fetchMemberFinancialDetails(user.id);
        setFinancialData(data);
        
        // Show success message with transfer details
        const details = response.transfer_details;
        alert(`Transfer successful!\nAmount: KES ${details.amount.toLocaleString()}\nTo: ${details.to_member.name}\nReference: ${details.reference}`);
      }
      
      return response;
    } catch (error) {
      console.error('Error transferring funds:', error);
      
      // Re-throw the error for the dialog to handle
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadFinancialData = async () => {
      setLoading(true);
      try {
        const data = await fetchMemberFinancialDetails(user.id);
        setFinancialData(data);
      } catch (error) {
        console.error('Error loading financial data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFinancialData();
  }, [user.id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleAllocateFunds = () => {
    setAllocateDialogOpen(true);
    console.log('Allocating funds:', financialData.unallocated_funds);
  };

  // FIXED processAllocation function
  const processAllocation = async (allocationData) => {
    try {
      console.log("Calling allocateFunds API with:", allocationData);
      
      const response = await allocateFunds(user.id, allocationData);
      console.log("API Response:", response);

      // Handle different response scenarios
      
      // Scenario 1: Performance warning that needs confirmation
      if (response.warning && response.needs_confirmation && !allocationData.confirmed) {
        console.log("Performance warning detected, throwing error for AllocateFundsDialog to handle");
        
        // Create an error object that mimics an HTTP error response
        const warningError = new Error("Performance warning");
        warningError.response = {
          data: response // Pass the entire response as error data
        };
        throw warningError; // This will be caught by AllocateFundsDialog
      }

      // Scenario 2: Successful allocation (either first time or after confirmation)
      if (response.success || response.confirmed || (!response.warning && !response.error)) {
        console.log("Allocation successful, refreshing financial data");
        
        // Refresh the financial data
        const data = await fetchMemberFinancialDetails(user.id);
        setFinancialData(data);
        
        // Show success message
        alert("Funds allocated successfully!");
        
        // Return success indicator
        return { success: true, confirmed: true };
      }

      // Scenario 3: Error response
      if (response.error) {
        throw new Error(response.error);
      }

      // Default success case (fallback)
      console.log("Default success case");
      const data = await fetchMemberFinancialDetails(user.id);
      setFinancialData(data);
      return { success: true };

    } catch (error) {
      console.error('Error in processAllocation:', error);

      // If it's a performance warning error, re-throw it for AllocateFundsDialog to handle
      if (error.response?.data?.warning && error.response?.data?.needs_confirmation) {
        console.log("Re-throwing performance warning error");
        throw error; // Let AllocateFundsDialog handle the warning dialog
      }
      
      // Handle other API errors
      if (error.response) {
        // API returned an error response
        const errorMessage = error.response.data?.error || error.response.data?.message || "Failed to allocate funds";
        console.error("API Error:", errorMessage);
        throw new Error(errorMessage);
      } else {
        // Network or other error
        console.error("Network/Other Error:", error.message);
        throw error;
      }
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        {/* Left side - Unallocated Funds and Action Buttons */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Available Actions
            </Typography>

            {/* Pending Allocation Section with Collect Cash button */}
            <Box sx={{
              mb: 2,
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: '1px dashed',
              borderColor: financialData.unallocated_funds > 0 ? 'primary.light' : 'grey.400',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Pending Allocation
                </Typography>
                <Typography variant="h5" color={financialData.unallocated_funds > 0 ? "primary.dark" : "text.secondary"}>
                  KES {financialData.unallocated_funds.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  Funds received but not yet distributed
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="success"
                startIcon={<AttachMoney />}
                size="medium"
                sx={{ height: 'fit-content' }}
                onClick={handleCashModalOpen}
              >
                Add Cash
              </Button>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AccountBalance />}
                fullWidth
                onClick={handleAllocateFunds}
                disabled={financialData.unallocated_funds <= 0}
              >
                ALLOCATE FUNDS
              </Button>

              <Button
                variant="outlined"
                color="primary"
                startIcon={<SwapHoriz />}
                fullWidth
                onClick={handleTransferDialogOpen}
                disabled={financialData.unallocated_funds <= 0}
              >
                TRANSFER FUNDS
              </Button>

              <Button
  variant="outlined"
  color="secondary"
  startIcon={<MoneyOff />}
  fullWidth
  onClick={handleOffsetDialogOpen}
  disabled={
    financialData.total_savings < (financialData.pending_loans + financialData.advance_loans) ||
    (financialData.pending_loans + financialData.advance_loans) <= 0
  }
>
  ACCOUNT OFFSET
</Button>
            </Box>
          </Paper>
        </Grid>

        {/* Right side - Financial Details in a more compact layout */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Financial Overview
            </Typography>

            {/* Savings & Loans Section - 2 columns */}
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>Savings & Loans</Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Box sx={{ pl: 1.5, borderLeft: 3, borderColor: 'success.main' }}>
                  <Typography variant="subtitle2">Total Savings</Typography>
                  <Typography variant="h6" color="success.main">
                    KES {financialData.total_savings.toLocaleString()}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ pl: 1.5, borderLeft: 3, borderColor: 'error.main' }}>
                  <Typography variant="subtitle2">Long-term Loans</Typography>
                  <Typography variant="h6" color="error.main">
                    KES {financialData.pending_loans.toLocaleString()}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ pl: 1.5, borderLeft: 3, borderColor: 'warning.main' }}>
                  <Typography variant="subtitle2">Advance Loans</Typography>
                  <Typography variant="h6" color="warning.main">
                    KES {financialData.advance_loans.toLocaleString()}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ pl: 1.5, borderLeft: 3, borderColor: 'info.main' }}>
                  <Typography variant="subtitle2">Loan Eligibility</Typography>
                  <Typography variant="h6" color="info.main">
                    KES {financialData.loan_eligibility.toLocaleString()}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 1.5 }} />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Credit Score</Typography>
              <LinearProgress
                variant="determinate"
                value={financialData.credit_score}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  mb: 0.5,
                  backgroundColor: '#e0e0e0'
                }}
              />
              <Typography variant="body2">{financialData.credit_score}% - {getCreditStatus(financialData.credit_score)}</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <AllocateFundsDialog
        open={allocateDialogOpen}
        onClose={() => setAllocateDialogOpen(false)}
        availableFunds={financialData.unallocated_funds}
        memberDetails={{
          id: user.id,
          name: user.name,
          member_id: user.member_id,
          ...user
        }}
        onAllocate={processAllocation}
      />

      <CashCollection
        open={cashModalOpen}
        onClose={handleCashModalClose}
        onSubmit={handleCashSubmit}
      />

      <TransferFundsDialog
        open={transferDialogOpen}
        onClose={handleTransferDialogClose}
        fromMember={{
          id: user.id,
          name: user.name,
          member_id: user.member_id
        }}
        availableFunds={financialData.unallocated_funds}
        onTransfer={handleTransferFunds}
      />

      <AccountOffsetDialog
        open={offsetDialogOpen}
        onClose={handleOffsetDialogClose}
        memberDetails={{
          id: user.id,
          name: user.name,
          member_id: user.member_id
        }}
        onOffsetComplete={handleOffsetComplete}
      />
    </>
  );
};

const getCreditStatus = (score) => {
  if (score >= 80) return "Excellent Standing";
  if (score >= 70) return "Good Standing";
  if (score >= 50) return "Fair Standing";
  return "Poor Standing";
};

export default FinanceDetails;